import { definitionSupportsRatioSize } from "%/dynamic-component-editor/options/ratio-size-support";
import {
  BRANDED_TYPE_BY_STYLE,
  TYPOGRAPHY_LABEL_BY_TYPE,
  TYPOGRAPHY_STYLE_LABELS,
} from "%/dynamic-component-editor/classes/branding/branded-text-styles";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import { assertDeclaredOptionWrite } from "%/dynamic-component-editor/helpers/declared-option-write";
import { camelToTitleCase, kebabToCamelCase, mergeArrayOfClassInstances } from "@bloglovin/vue-framework";
import BaseSingletonEntity from "%/dynamic-component-editor/classes/base-singleton-entity";
import DynamicOption from "%/dynamic-component-editor/classes/dynamic-option";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import Theme from "%/dynamic-component-editor/classes/theme";
import { createOptionClass } from "%/dynamic-component-editor/inspector/helpers/option-helper";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import OptionsMap from "%/dynamic-component-editor/options/options-map";
import Positioning from "%/dynamic-component-editor/options/positioning";
import { ensurePositioningOptionClass } from "%/utils/universal-element-options";
import App from "@bloglovin/inspire-engine/App";
import { getCompiledElement, getCompiledElementDcId } from "%/dynamic-components/render-engine/compiled-payload-store";
import { isCompiledRenderSurface } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { aliasedElementType } from "%/dynamic-component-editor/helpers/deprecated-element-type-aliases";

// eslint-disable-next-line global-require
const useStagingFrameStore = (...args) => require("%/dynamic-component-editor/stores/staging-frame-store").useStagingFrameStore(...args);
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);
const { overridesForBreakpoint } = require("%/dynamic-component-editor/inspector/helpers/overridden-option-keys");
// eslint-disable-next-line global-require

const OPTIONS_SAVE_DEBOUNCE_MS = 400;

const NON_ENTITY_LEGACY_GROUPS = new Set(["general", "layout", "text", "payments"]);

function bareEntityType(value) {
  return typeof value === "string" ? value.replace(/^Legacy/, "") : value;
}

function capitalizeFirst(value) {
  return typeof value === "string" && value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function withExplicitContextBinding(options, binding) {
  let tree = options;
  if (typeof tree === "string") {
    try {
      tree = JSON.parse(tree);
    } catch {
      tree = null;
    }
  }
  if (!tree || typeof tree !== "object" || Array.isArray(tree)) {
    tree = {};
  }
  if (!tree.context?.entityContext) {
    tree.context = { appName: "Legacy", entityContext: binding.contextName, entityType: binding.entityType };
  }
  return tree;
}

class DynamicElement extends BaseSingletonEntity {
  static #allowConstruction = false;
  static #elementDefinitions = {};
  static #allElementOptionClasses = {};
  static #optionClassesByKey = {};
  static #entityBindingClassIndex = null;

  #id;
  #workingOptionsTree = null;
  #workingOptionsBase = null;
  #overriddenSet = null;
  #overriddenSetBase = undefined;
  #optionsSaveTimer = null;
  #optimisticWriteHandle = null;
  #optimisticWriteTarget = null;
  #undoBeforeOptions = null;
  #brandingHasValueCache = new Map();
  #brandingCacheVersion = 0;
  #overrideBadgeCache = new Map();
  #overrideBadgeBase = null;
  #overrideBadgeOverriddenBase = null;
  #overrideBadgeBrandingVersion = -1;
  static #globalBrandingVersion = 0;

  static #mergeDefinitionInheritance(definition, collected = { options: [], optionBundles: [] }) {
    if (!definition) {
      return collected;
    }
    this.#mergeDefinitionInheritance(definition.extends, collected);
    (definition.mixins || []).forEach(mixin => {
      this.#mergeDefinitionInheritance(mixin, collected);
    });
    collected.options = mergeArrayOfClassInstances(collected.options, definition.options ?? []);
    collected.optionBundles = mergeArrayOfClassInstances(collected.optionBundles, definition.optionBundles ?? []);
    return collected;
  }

  static #withoutShadowedBundles(bundles) {
    const seenToolGroups = new Set();
    return [...bundles].reverse().filter(bundle => {
      const instance = typeof bundle === "function" ? new bundle() : bundle;
      if (!instance.userInterfaceComponent || !instance.inspectorToolGroup) {
        return true;
      }
      if (seenToolGroups.has(instance.inspectorToolGroup)) {
        return false;
      }
      seenToolGroups.add(instance.inspectorToolGroup);
      return true;
    }).reverse();
  }

  static registerElementDefinitions(elementsArray) {
    elementsArray.forEach(element => {
      if (element && element.name) {
        const hasOwnSurface = (element.options?.length ?? 0) > 0 || (element.optionBundles?.length ?? 0) > 0;
        if (!hasOwnSurface && (element.extends || (element.mixins || []).length > 0)) {
          const inherited = this.#mergeDefinitionInheritance(element);
          element.options = inherited.options;
          element.optionBundles = this.#withoutShadowedBundles(inherited.optionBundles);
        }
        if (this.#elementDefinitions[element.name] !== element) {
          delete this.#allElementOptionClasses[element.name];
          delete this.#optionClassesByKey[element.name];
        }
        this.#elementDefinitions[element.name] = element;
      }
    });
  }

  static getElementDefinition(elementType) {
    return this.#elementDefinitions[aliasedElementType(elementType)];
  }

  static supportsRatioSize(elementType) {
    return definitionSupportsRatioSize(this.getElementDefinition(elementType));
  }

  static getOptionClass(elementType, optionKey) {
    if (!this.#optionClassesByKey[elementType]) {
      this.cacheOptionClassesForElementType(elementType);
    }
    return this.#optionClassesByKey[elementType]?.[optionKey];
  }

  static getOptionClasses(elementType) {
    return (this.cacheOptionClassesForElementType(elementType) || []).filter(optClass => optClass.optionKey);
  }

  static #collectOptionClassesFromDefinition(definition, collected = []) {
    if (!definition) {
      return collected;
    }
    this.#collectOptionClassesFromDefinition(definition.extends, collected);
    (definition.mixins || []).forEach(mixin => {
      this.#collectOptionClassesFromDefinition(mixin, collected);
    });
    (definition.options || []).forEach(OptionClass => {
      collected.push(typeof OptionClass === "function" ? new OptionClass() : OptionClass);
    });
    (definition.optionBundles || []).forEach(BundleClass => {
      const bundle = typeof BundleClass === "function" ? new BundleClass() : BundleClass;
      bundle.options.forEach(OptionClass => {
        collected.push(typeof OptionClass === "function" ? new OptionClass() : OptionClass);
      });
    });
    return collected;
  }

  static #declaresAnyOptions(definition) {
    if (!definition) {
      return false;
    }
    if ((definition.options || []).length > 0 || (definition.optionBundles || []).length > 0) {
      return true;
    }
    return this.#declaresAnyOptions(definition.extends)
      || (definition.mixins || []).some(mixin => this.#declaresAnyOptions(mixin));
  }

  static cacheOptionClassesForElementType(elementType) {
    if (this.#allElementOptionClasses[elementType]) {
      return this.#allElementOptionClasses[elementType];
    }

    const elementDef = this.getElementDefinition(elementType);
    if (!elementDef) {
      return [];
    }

    const ownClasses = this.#collectOptionClassesFromDefinition(elementDef);

    if (ownClasses.length === 0 && this.#declaresAnyOptions(elementDef)) {
      console.warn( // eslint-disable-line no-console
        `DynamicElement.cacheOptionClassesForElementType: definition for "${elementType}" declares options or `
        + "optionBundles but NONE resolved to an option class — those options will be missing from the inspector "
        + "and their defaults will never be seeded. Check for a circular import in the declaring file.",
      );
    }

    const allClasses = ensurePositioningOptionClass(ownClasses, Positioning);

    this.#allElementOptionClasses[elementType] = allClasses;
    this.#optionClassesByKey[elementType] = {};

    allClasses.forEach(optClass => {
      if (optClass.optionKey) {
        this.#optionClassesByKey[elementType][optClass.optionKey] = optClass;
      }
    });

    return allClasses;
  }

  static isElementOfTypeLayout(type) {
    return [
      "VerticalContainer",
      "HorizontalContainer",
      "Container",
      "CarouselContainer",
      "VerticalContainerWrapper",
      "HorizontalContainerWrapper",
      "LinkContainer",
      "EntityActionContainer",
      "FileUploadContainer",
      "SubscriptionPlanSheet",
    ].includes(type);
  }

  static getChildElements(parentId) {
    if (isCompiledRenderSurface()) {
      return (getCompiledElement(parentId)?.childrenIds ?? []).map(id => DynamicElement.get(String(id)));
    }
    const app = App.get("CreatorStudio");
    return app.getFeedItems("Element", { id: String(parentId) }, "childElements")
        .map(item => DynamicElement.get(String(item.entityPk.id)));
  }

  static getSiblingAtIndex(parentId, index) {
    if (index < 0) {
      return;
    }
    const children = DynamicElement.getChildElements(parentId);
    return children.find(child => child.indexInParent === index);
  }

  static serializeElementSubtree(dynamicElement) {
    const entity = dynamicElement?.element;
    if (!entity?.type) {
      return null;
    }
    const childIds = dynamicElement.childElementIds ?? [];
    const contents = [];
    for (const childId of childIds) {
      const child = DynamicElement.serializeElementSubtree(DynamicElement.get(childId));
      if (child) {
        contents.push(child);
      }
    }
    let options = entity.options ?? {};
    const binding = dynamicElement.entityBinding;
    if (binding?.source === "class") {
      options = withExplicitContextBinding(options, binding);
    }
    return {
      type: entity.type,
      displayName: entity.displayName ?? "",
      options,
      contents,
    };
  }

  static entityBindingClassIndex() {
    if (DynamicElement.#entityBindingClassIndex) {
      return DynamicElement.#entityBindingClassIndex;
    }
    // eslint-disable-next-line global-require
    const LEGACY_ENTITIES = require("%/dynamic-component-editor/constants/legacy-entities").default;
    const index = new Map();
    for (const [key, LegacyClass] of Object.entries(LEGACY_ENTITIES)) {
      if (NON_ENTITY_LEGACY_GROUPS.has(key)) {
        continue;
      }
      let raw = null;
      try {
        raw = LegacyClass.elements ?? new LegacyClass(key).elements;
      } catch {
        continue;
      }
      if (!raw) {
        continue;
      }
      const entityType = capitalizeFirst(key);
      for (const component of Object.values(raw)) {
        const name = component?.name;
        if (typeof name === "string" && name && !index.has(name)) {
          index.set(name, { contextName: key, entityType });
        }
      }
    }
    DynamicElement.#entityBindingClassIndex = index;
    return index;
  }

  static resolveDefinitionName(elementDef) {
    if (!elementDef) {
      return;
    }
    return elementDef.elementPanelSettings?.alias
        || elementDef.elementPanelSettings?.displayName
        || camelToTitleCase(elementDef.name);
  }

  static defaultOptionValue(optionsTree, optionKey) {
    if (!optionsTree || typeof optionsTree !== "object") {
      return undefined;
    }
    const slots = [
      optionsTree["light-mode"]?.default?.default,
      optionsTree["dark-mode"]?.default?.default,
      optionsTree.default?.default,
    ];
    for (const slot of slots) {
      const leaf = slot?.[optionKey];
      if (leaf === null || leaf === undefined) {
        continue;
      }
      return typeof leaf === "object" ? leaf.r : leaf;
    }
    return undefined;
  }

  static buildDefaultOptionsTree(elementType, context) {
    this.cacheOptionClassesForElementType(elementType);
    const optionClasses = DynamicOption.getAllOptionClassesForElement(elementType, context ?? {});
    const themeId = context?.themeId ?? null;
    const brandingTarget = this.getBrandingTargetForElementType(elementType);
    const dynamicComponent = context?.dynamicComponent ?? null;
    const tree = {};

    for (const themeMode of ["light-mode", "dark-mode"]) {
      for (const optionClass of optionClasses) {
        const leaf = this.buildDefaultLeafForElement(
          brandingTarget,
          optionClass,
          themeId,
          themeMode,
          dynamicComponent,
        );
        if (!leaf) {
          continue;
        }
        tree[themeMode] = tree[themeMode] || {};
        tree[themeMode].default = tree[themeMode].default || {};
        tree[themeMode].default.default = tree[themeMode].default.default || {};
        tree[themeMode].default.default[optionClass.optionKey] = leaf;
      }
    }

    return tree;
  }

  static getBrandingTargetForElementType(
    elementType,
    elementData = null,
    dynamicComponent = null,
    variableOverrides = null,
    breakpoint = "default",
    state = "default",
    context = null,
  ) {
    const elementDef = DynamicElement.getElementDefinition(elementType);
    const BrandingClass = elementDef?.brandingType ?? null;
    if (!BrandingClass) {
      return null;
    }
    try {
      const instance = new BrandingClass();
      const probeConfig = {
        type: elementType,
        options: this.#buildBrandingProbeOptions(
          elementData, dynamicComponent, variableOverrides, breakpoint, state, context, elementType,
        ),
      };
      let chainLookupType = elementType;
      try {
        const derived = instance.determineType(probeConfig, "default", "default", []);
        const ownsDerived = derived
          && typeof BrandingClass.ownsElementType === "function"
          && BrandingClass.ownsElementType(derived);
        if (ownsDerived) {
          chainLookupType = derived;
        }
      } catch { // eslint-disable-line no-empty
      }
      if (chainLookupType === elementType
        && typeof BrandingClass.ownsElementType === "function"
        && !BrandingClass.ownsElementType(elementType)) {
        chainLookupType = BrandingClass.elementTypes?.[0]?.type ?? elementType;
      }
      return {
        BrandingClass,
        instance,
        type: chainLookupType,
        configType: BrandingClass.brandingConfigKey ?? chainLookupType,
        variant: instance.determineVariant(probeConfig, "default", "default", []),
        size: instance.determineSize(probeConfig, "default", "default", []),
      };
    } catch {
      return null;
    }
  }

  static #buildBrandingProbeOptions(
    elementData,
    dynamicComponent = null,
    variableOverrides = null,
    breakpoint = "default",
    state = "default",
    context = null,
    elementType = null,
  ) {
    const tree = elementData?.options;
    const flat = {};
    const slot = tree && typeof tree === "object"
      ? tree["light-mode"]?.default?.default ?? tree["dark-mode"]?.default?.default ?? null
      : null;
    if (slot && typeof slot === "object") {
      for (const optionKey of Object.keys(slot)) {
        const leaf = slot[optionKey];
        if (!leaf || typeof leaf !== "object") {
          continue;
        }
        if (leaf.r !== undefined) {
          flat[`default.${optionKey}`] = leaf.r;
        } else if (leaf.c !== undefined) {
          flat[`default.${optionKey}`] = leaf.c;
        }
      }
    }
    if (context && elementData) {
      /* eslint-disable global-require */
      const DynamicOptionModule = require("%/dynamic-component-editor/classes/dynamic-option");
      /* eslint-enable global-require */
      const DynamicOption = DynamicOptionModule.default;
      for (const determiningKey of [
        "button-type",
        "button-size",
        "styled-text-variant",
        "text-style-option",
        "text-branding-style-option",
      ]) {
        try {
          let resolvedValue = DynamicOption.getRawOptionValue(elementData, determiningKey, breakpoint, state, context);
          if (resolvedValue === undefined) {
            resolvedValue = DynamicOption.getCompiledOptionValue(elementData, determiningKey, breakpoint, state, context);
          }
          if (resolvedValue !== undefined) {
            flat[`default.${determiningKey}`] = resolvedValue;
          }
        } catch {
          continue;
        }
      }
    }
    const elementId = elementData?.id;
    if (
      dynamicComponent
      && elementId !== undefined
      && elementId !== null
      && typeof dynamicComponent.getVariableBindingForOption === "function"
      && typeof dynamicComponent.getVariableValue === "function"
    ) {
      for (const optionKey of ["button-type", "button-size", "styled-text-variant"]) {
        const variableId = dynamicComponent.getVariableBindingForOption(elementId, optionKey);
        if (variableId === null || variableId === undefined) {
          continue;
        }
        const override = variableOverrides?.[variableId];
        const raw = override !== undefined ? override : dynamicComponent.getVariableValue(variableId);
        if (raw !== undefined) {
          flat[`default.${optionKey}`] = raw;
        }
      }
    }
    const resolvedElementType = elementType ?? elementData?.type ?? null;
    if (resolvedElementType) {
      for (const determiningKey of [
        "button-type",
        "button-size",
        "styled-text-variant",
        "text-style-option",
        "text-branding-style-option",
      ]) {
        if (flat[`default.${determiningKey}`] !== undefined) {
          continue;
        }
        const seeded = this.#optionDefaultForElementType(resolvedElementType, determiningKey);
        if (seeded !== undefined && seeded !== "") {
          flat[`default.${determiningKey}`] = seeded;
        }
      }
    }
    return flat;
  }

  static #optionDefaultForElementType(elementType, optionKey) {
    // eslint-disable-next-line global-require
    const DynamicOption = require("%/dynamic-component-editor/classes/dynamic-option").default;
    const optionClass = DynamicOption
        .getAllOptionClassesForElement(elementType, {})
        .find(option => option.optionKey === optionKey);
    if (!optionClass) {
      return undefined;
    }
    try {
      return optionClass.getValueOrDefault({})[`default.${optionKey}`];
    } catch {
      return undefined;
    }
  }

  static buildBrandingDefaultLeaf(brandingTarget, optionClass, themeId, themeMode, themeElementResolver = null, state = "default") {
    if (!brandingTarget) {
      return undefined;
    }
    const { BrandingClass, instance, type, configType, variant, size } = brandingTarget;

    let isOwned;
    try {
      isOwned = typeof BrandingClass.isThemableOptionKey === "function"
        && BrandingClass.isThemableOptionKey(optionClass.optionKey);
    } catch {
      return undefined;
    }
    if (!isOwned) {
      return undefined;
    }

    const lookup = this.#normaliseThemeElementResolver(themeElementResolver);
    let rawValue;
    if (lookup) {
      rawValue = this.#readBrandingFromThemeElements(
        lookup, BrandingClass, type, variant, size, optionClass.optionKey, themeMode, state,
      );
    }

    if (rawValue === undefined && state === "default") {
      try {
        rawValue = instance.getRawValue(
          optionClass.optionKey,
          "default",
          "default",
          configType ?? type,
          variant,
          size,
          [],
          {},
        );
      } catch {
        return undefined;
      }
    }
    if (rawValue === undefined) {
      return undefined;
    }

    let compiled;
    try {
      compiled = optionClass.constructor.convertConfigValueToCompiledValue(
        rawValue, themeId, themeMode,
      );
    } catch {
      compiled = undefined;
    }
    return { r: rawValue, c: compiled, i: true };
  }

  static #normaliseThemeElementResolver(input) {
    if (!input) {
      return null;
    }
    if (typeof input === "function") {
      return input;
    }
    if (typeof input.getThemeElementFor === "function") {
      return (type, variant, size) => input.getThemeElementFor(type, variant, size);
    }
    return null;
  }

  static #readBrandingFromThemeElements(themeElementResolver, BrandingClass, type, variant, size, optionKey, themeMode, state = "default") {
    const maxHops = 5;
    let currentVariant = variant;
    let currentSize = size;
    for (let hop = 0; hop <= maxHops; hop++) {
      const themeElement = themeElementResolver(type, currentVariant, currentSize);
      if (themeElement) {
        const leafValue = this.#readOptionLeafFromThemeElement(
          themeElement, optionKey, themeMode, BrandingClass, currentVariant, currentSize, state,
        );
        if (leafValue !== undefined) {
          return leafValue;
        }
      }
      if (typeof BrandingClass.prototype.findNextInheritanceLevel !== "function") {
        return undefined;
      }
      let next;
      try {
        next = new BrandingClass().findNextInheritanceLevel(currentVariant, currentSize);
      } catch {
        return undefined;
      }
      if (!next) {
        return undefined;
      }
      const nextSize = next.size ?? next.type ?? null;
      if (next.variant === currentVariant && nextSize === currentSize) {
        return undefined;
      }
      currentVariant = next.variant;
      currentSize = next.size ?? next.type ?? null;
    }
    return undefined;
  }

  static #readOptionLeafFromThemeElement(brandingElement, optionKey, themeMode, BrandingClass, currentVariant, currentSize, state = "default") {
    if (!brandingElement) {
      return undefined;
    }
    const tree = brandingElement.parsedOptions ?? {};
    if (state !== "default") {
      const leafAtState = tree?.[themeMode]?.default?.[state]?.[optionKey];
      const leafResolved = this.#pickBrandingLeafForState(leafAtState, null, BrandingClass, currentVariant, currentSize);
      if (leafResolved) {
        if (leafResolved.r !== undefined) {
          return leafResolved.r;
        }
        if (leafResolved.c !== undefined) {
          return leafResolved.c;
        }
      }
      const leafAtDefault = tree?.[themeMode]?.default?.default?.[optionKey];
      if (leafAtDefault && leafAtDefault.i !== true) {
        if (leafAtDefault.r !== undefined) {
          return leafAtDefault.r;
        }
        if (leafAtDefault.c !== undefined) {
          return leafAtDefault.c;
        }
      }
      return undefined;
    }
    const leafAtDefault = tree?.[themeMode]?.default?.default?.[optionKey];
    const leaf = this.#pickBrandingLeafForState(null, leafAtDefault, BrandingClass, currentVariant, currentSize);
    if (!leaf) {
      return undefined;
    }
    if (leaf.r !== undefined) {
      return leaf.r;
    }
    if (leaf.c !== undefined) {
      return leaf.c;
    }
    return undefined;
  }

  static #pickBrandingLeafForState(leafAtState, leafAtDefault, BrandingClass, currentVariant, currentSize) {
    const isBase = !BrandingClass || this.#isBaseBrandingCoordinate(BrandingClass, currentVariant, currentSize);
    if (leafAtState && (leafAtState.i !== true || isBase)) {
      return leafAtState;
    }
    if (leafAtDefault && (leafAtDefault.i !== true || isBase)) {
      return leafAtDefault;
    }
    return null;
  }

  static #isBaseBrandingCoordinate(BrandingClass, variant, size) {
    const base = BrandingClass?.inheritanceStructure?.base;
    if (!base) {
      return true;
    }
    const [baseVariant, baseSize] = base.split(".");
    if (baseSize) {
      const normalizedSize = size === undefined || size === null || size === "" ? null : size;
      return variant === baseVariant && normalizedSize === baseSize;
    }
    return variant === baseVariant;
  }

  static buildDefaultLeafForElement(brandingTarget, optionClass, themeId, themeMode, dynamicComponent = null) {
    const brandingLeaf = this.buildBrandingDefaultLeaf(
      brandingTarget, optionClass, themeId, themeMode, dynamicComponent,
    );
    if (brandingLeaf && brandingLeaf.c !== undefined) {
      return brandingLeaf;
    }
    let classLeaf;
    try {
      classLeaf = DynamicOption.buildClassDefaultValue(optionClass, themeId, themeMode);
    } catch {
      return undefined;
    }
    if (classLeaf && classLeaf.c !== undefined) {
      return classLeaf;
    }
    return undefined;
  }

  static get(id) {
    // eslint-disable-next-line global-require
    const MultiDynamicElement = require("%/dynamic-component-editor/classes/multi-dynamic-element");
    if (MultiDynamicElement.isCompositeId(id)) {
      const members = MultiDynamicElement.parseCompositeId(id)
          .map(bare => DynamicElement.get(bare))
          .filter(member => member
            && member.isTheme !== true
            && member.element?.isEntityPkTemp?.() !== true);
      if (members.length <= 1) {
        return members[0] ?? null;
      }
      return new MultiDynamicElement(id, members);
    }

    const instanceKey = `dynamic-element-${id}`;
    // eslint-disable-next-line global-require
    const BrandingElement = require("%/dynamic-component-editor/classes/branding-element").default;

    const instance = BaseSingletonEntity._getInstance(DynamicElement, instanceKey, () => {
      DynamicElement.#allowConstruction = true;
      const entity = isCompiledRenderSurface()
        ? null
        : App.get("CreatorStudio").getEntity("Element", { id: String(id).split(":")[0] });
      const ElementClass = entity?.source === "theme" ? BrandingElement : DynamicElement;
      const newInstance = new ElementClass(id);
      DynamicElement.#allowConstruction = false;
      return newInstance;
    });

    if (instance.element?.type) {
      DynamicElement.cacheOptionClassesForElementType(instance.element.type);
    }
    return instance;
  }

  get isDestination() {
    return true;
  }

  get isTheme() {
    return false;
  }

  constructor(id) {
    super();
    if (!DynamicElement.#allowConstruction) {
      throw new DynamicComponentError("Use DynamicElement.get() instead.", {});
    }
    this.#id = id?.toString().split(":")[0];
  }

  get id() {
    return this.#id;
  }

  get element() {
    if (isCompiledRenderSurface()) {
      const node = getCompiledElement(this.#id);
      if (!node) {
        return null;
      }
      const dcId = getCompiledElementDcId(this.#id);
      return {
        id: this.#id,
        type: node.type,
        dynamicComponent: dcId === null ? undefined : { pk: { id: dcId } },
      };
    }
    return App.get("CreatorStudio").getEntity("Element", { id: this.#id });
  }

  get type() {
    return this.element?.type;
  }

  get entityBinding() {
    const optionContext = this.parsedOptions?.context;
    if (optionContext?.entityContext) {
      return {
        contextName: optionContext.entityContext,
        entityType: bareEntityType(optionContext.entityType ?? null) || null,
        field: optionContext.field ?? optionContext.actionName ?? null,
        source: "options",
      };
    }
    return null;
  }

  get parentElementId() {
    return this.element?.parentElement?.pk?.id;
  }

  get dynamicComponentId() {
    return this.element?.dynamicComponent?.pk?.id;
  }

  get destinationId() {
    return this.element?.destination?.pk?.id;
  }

  get indexInParent() {
    return this.element?.indexInParent ?? -1;
  }

  get childElementIds() {
    if (isCompiledRenderSurface()) {
      return (getCompiledElement(this.#id)?.childrenIds ?? []).map(String);
    }
    return App.get("CreatorStudio")
        .getFeedItems("Element", { id: String(this.#id) }, "childElements")
        .map(item => String(item.entityPk?.id))
        .filter(Boolean)
        .sort((a, b) => {
          const indexDiff = DynamicElement.get(a).indexInParent - DynamicElement.get(b).indexInParent;
          return indexDiff !== 0 ? indexDiff : Number(a) - Number(b);
        });
  }

  get elementDef() {
    return this.type ? DynamicElement.getElementDefinition(this.type) : null;
  }

  get parsedOptions() {
    const raw = this.element?.options;
    if (this.#workingOptionsTree !== null && raw !== this.#workingOptionsBase && this.#hasUnsavedOptions()) {
      this.#workingOptionsBase = raw;
      return this.#workingOptionsTree;
    }
    if (raw !== this.#workingOptionsBase || this.#workingOptionsTree === null) {
      this.#workingOptionsBase = raw;
      this.#workingOptionsTree = this.#pruneCorruptTreeKeys(DynamicOption.parseOptions(raw));
    }
    return this.#workingOptionsTree;
  }

  get displayName() {
    const brandedStyleDisplayName = this.#brandedTypographyStyleName();
    const stored = this.element?.displayName;
    if (stored && !(brandedStyleDisplayName && TYPOGRAPHY_STYLE_LABELS.has(stored))) {
      return stored;
    }
    if (brandedStyleDisplayName) {
      return brandedStyleDisplayName;
    }
    if (this.type === "DynamicComponentElement") {
      const referencedName = this.#referencedDynamicComponentDisplayName();
      if (referencedName) {
        return referencedName;
      }
    }
    if (!this.elementDef) {
      return this.type;
    }
    return this.elementDef.elementPanelSettings?.alias
      || this.elementDef.elementPanelSettings?.displayName
      || camelToTitleCase(this.elementDef.name);
  }

  #brandedTypographyStyleName() {
    const labelForType = TYPOGRAPHY_LABEL_BY_TYPE[this.type];
    if (labelForType === undefined) {
      return null;
    }
    const style = DynamicElement.defaultOptionValue(this.parsedOptions, "text-style-option")
      ?? DynamicElement.defaultOptionValue(this.parsedOptions, "text-branding-style-option");
    const styledType = BRANDED_TYPE_BY_STYLE[style];
    return TYPOGRAPHY_LABEL_BY_TYPE[styledType] ?? labelForType;
  }

  #referencedDynamicComponentDisplayName() {
    const referencedId = Number(DynamicElement.defaultOptionValue(this.parsedOptions, "referenced-component-id") ?? 0);
    if (!Number.isFinite(referencedId) || referencedId === 0) {
      return null;
    }
    const referenced = DynamicComponent.get(referencedId);
    const componentName = referenced?.getRootOption?.("component-name");
    return typeof componentName === "string" && componentName.length > 0 ? componentName : null;
  }

  sanitize() {
    const elementType = this.type;
    const childrenIds = this.childElementIds;
    if (!elementType) {
      const minimized = DynamicOption.minimize(this.parsedOptions);
      DynamicElement.carryContextBinding(this.parsedOptions, minimized);
      return { type: elementType, options: minimized, childrenIds };
    }

    const dynamicComponent = DynamicComponent.get(this.dynamicComponentId);
    const themeId = dynamicComponent?.themeId ?? null;
    const theme = dynamicComponent?.theme ?? null;

    DynamicElement.cacheOptionClassesForElementType(elementType);

    const compiledTree = {};
    const parsedOptions = this.parsedOptions ?? {};
    for (const themeMode of Object.keys(parsedOptions)) {
      const modeTree = parsedOptions[themeMode];
      if (!modeTree || typeof modeTree !== "object") {
        continue;
      }
      for (const breakpoint of Object.keys(modeTree)) {
        const breakpointTree = modeTree[breakpoint];
        if (!breakpointTree || typeof breakpointTree !== "object") {
          continue;
        }
        for (const state of Object.keys(breakpointTree)) {
          const stateTree = breakpointTree[state];
          if (!stateTree || typeof stateTree !== "object") {
            continue;
          }
          for (const optionKey of Object.keys(stateTree)) {
            const leaf = stateTree[optionKey];
            if (!leaf || typeof leaf !== "object") {
              continue;
            }
            let compiledValue;
            let compileError = null;
            try {
              compiledValue = DynamicOption.getCompiledOptionValue(
                this.#elementDataForOption(parsedOptions),
                optionKey,
                breakpoint,
                state,
                { themeMode, themeId, theme, dynamicComponent },
              );
            } catch (error) {
              compileError = error;
              compiledValue = undefined;
            }
            if (compiledValue === undefined) {
              compiledValue = leaf.c;
            }
            const inheritsFromBrandingChain = leaf.i === true && leaf.r === undefined;
            if (compiledValue === undefined && inheritsFromBrandingChain) {
              continue;
            }
            if (compiledValue === undefined) {
              const optionClass = DynamicElement.getOptionClass(elementType, optionKey);
              const rawValue = leaf.r;
              let reason;
              if (compileError) {
                reason = `compile threw: ${compileError.message}`;
              } else if (!optionClass) {
                reason = "no option class is registered for this key on this element type";
              } else {
                reason = `${optionClass.constructor.name}.getCompiledOptionValue returned undefined for raw value `
                  + `${JSON.stringify(rawValue)} and the persisted leaf carried no compiled value `
                  + `(leaf keys: ${Object.keys(leaf).join(",") || "none"})`;
              }
              console.warn( // eslint-disable-line no-console
                `DynamicElement.sanitize: option "${optionKey}" (${themeMode}/${breakpoint}/${state}) on element `
                + `${this.id} (${elementType}) produced no compiled value and was DROPPED from the release — ${reason}`,
              );
              continue;
            }
            compiledTree[themeMode] = compiledTree[themeMode] || {};
            compiledTree[themeMode][breakpoint] = compiledTree[themeMode][breakpoint] || {};
            compiledTree[themeMode][breakpoint][state] = compiledTree[themeMode][breakpoint][state] || {};
            const compiledLeaf = { c: compiledValue };
            if (leaf.i === true) {
              compiledLeaf.i = true;
            }
            compiledTree[themeMode][breakpoint][state][optionKey] = compiledLeaf;
          }
        }
      }
    }

    const brandingTarget = DynamicElement.getBrandingTargetForElementType(
      elementType,
      this.#elementDataForOption(parsedOptions),
    );
    const optionClasses = DynamicOption.getAllOptionClassesForElement(elementType, {});
    for (const themeMode of ["light-mode", "dark-mode"]) {
      for (const optionClass of optionClasses) {
        const existing = compiledTree[themeMode]?.default?.default?.[optionClass.optionKey];
        if (existing !== undefined) {
          continue;
        }
        const defaultLeaf = DynamicElement.buildDefaultLeafForElement(
          brandingTarget, optionClass, themeId, themeMode, dynamicComponent,
        );
        if (!defaultLeaf || defaultLeaf.c === undefined) {
          continue;
        }
        compiledTree[themeMode] = compiledTree[themeMode] || {};
        compiledTree[themeMode].default = compiledTree[themeMode].default || {};
        compiledTree[themeMode].default.default = compiledTree[themeMode].default.default || {};
        const paddedLeaf = { c: defaultLeaf.c };
        if (defaultLeaf.i === true) {
          paddedLeaf.i = true;
        }
        compiledTree[themeMode].default.default[optionClass.optionKey] = paddedLeaf;
      }
    }

    DynamicElement.carryContextBinding(parsedOptions, compiledTree);

    return { type: elementType, options: compiledTree, childrenIds };
  }

  static carryContextBinding(sourceOptions, targetOptions) {
    const context = sourceOptions?.context;
    if (context && typeof context === "object" && !Array.isArray(context)) {
      targetOptions.context = context;
    }
  }

  updateDisplayName(value) {
    if (!this.element || this.element.isEntityPkTemp?.()) {
      return;
    }
    App.get("CreatorStudio").triggerEvent("UpdateElementTitle", {
      element: { id: this.element.id },
      displayName: value,
    });
  }

  rebindEntityContext(contextName, options = this.parsedOptions) {
    const currentContext = options?.context;
    if (typeof contextName !== "string" || contextName === "") {
      console.error( // eslint-disable-line no-console
        "rebindEntityContext: refused a non-string context name — the binding was left unchanged",
        { elementId: this.element?.id, received: contextName },
      );
      return;
    }
    if (!this.element) {
      return;
    }
    const binding = this.entityBinding;
    const baseContext = currentContext?.entityContext
      ? currentContext
      : binding ? { appName: "Legacy", entityContext: binding.contextName, entityType: binding.entityType } : null;
    if (!baseContext) {
      return;
    }
    if (baseContext.entityContext === contextName) {
      return;
    }
    this.replaceOptions({ ...options, context: { ...baseContext, entityContext: contextName } });
  }

  replaceOptions(tree) {
    if (!this.element) {
      return;
    }
    App.get("CreatorStudio").triggerUndoableEvent("UpdateElement", {
      element: { id: this.element.id },
      options: tree,
      optionsVersion: this.element.optionsVersion ?? 0,
      type: this.type,
      displayName: this.element.displayName,
      overriddenOptionKeys: this.element.overriddenOptionKeys ?? "[]",
    });
  }

  modifyType(type, options = null) {
    if (!this.element) {
      return;
    }
    this.#flushOptimisticOptionsWriteNow();
    App.get("CreatorStudio").triggerUndoableEvent("UpdateElement", {
      element: { id: this.element.id },
      type,
      displayName: this.element.displayName ?? null,
      options: options ?? this.element.options ?? null,
      optionsVersion: this.element.optionsVersion ?? 0,
      overriddenOptionKeys: this.element.overriddenOptionKeys ?? "[]",
    });
  }

  determineIfOptionsAreInheriting(optionKeys, breakpoint, state) {
    const data = this.#elementDataForOption();
    if (!data) {
      return false;
    }
    return DynamicOption.determineIfOptionsAreInheriting(
      data, optionKeys, this.#breakpointAlias(breakpoint), state, this.#optionContext(),
    );
  }

  determineIfOptionsAreOverwritten(optionKeys, breakpoint, state) {
    const data = this.#elementDataForOption();
    if (!data) {
      return false;
    }
    return DynamicOption.determineIfOptionsAreOverwritten(
      data, optionKeys, this.#breakpointAlias(breakpoint), state, this.#optionContext(),
    );
  }

  get overriddenOptionKeysSet() {
    const raw = this.element?.overriddenOptionKeys ?? "";
    if (raw !== this.#overriddenSetBase || this.#overriddenSet === null) {
      this.#overriddenSetBase = raw;
      let list = [];
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            list = parsed;
          }
        } catch {
          list = [];
        }
      }
      this.#overriddenSet = new Set(list.filter(key => !String(key).includes("[object ")));
    }
    return this.#overriddenSet;
  }

  isOptionOverridden(optionKey, breakpoint = "default", state = "default") {
    const bp = this.#breakpointAlias(breakpoint);
    return this.overriddenOptionKeysSet.has(`${state}:${bp}:${optionKey}`);
  }

  hasBrandingFallback(optionKey, breakpoint = "default", state = "default", themeMode = "light-mode") {
    if (DynamicElement.#globalBrandingVersion !== this.#brandingCacheVersion) {
      this.#brandingHasValueCache.clear();
      this.#brandingCacheVersion = DynamicElement.#globalBrandingVersion;
    }
    const bp = this.#breakpointAlias(breakpoint);
    const cacheKey = `${themeMode}:${state}:${bp}:${optionKey}`;
    if (this.#brandingHasValueCache.has(cacheKey)) {
      return this.#brandingHasValueCache.get(cacheKey);
    }
    const data = this.#elementDataForOption();
    if (!data) {
      this.#brandingHasValueCache.set(cacheKey, false);
      return false;
    }
    const ctx = this.#optionContext();
    const value = DynamicOption.computeBrandingFallback(data, optionKey, bp, state, { ...ctx, themeMode });
    const has = !!value;
    this.#brandingHasValueCache.set(cacheKey, has);
    return has;
  }

  static bumpBrandingVersion() {
    DynamicElement.#globalBrandingVersion += 1;
  }

  static get globalBrandingVersion() {
    return DynamicElement.#globalBrandingVersion;
  }

  overrideBadgesForBreakpoint(breakpointAlias) {
    const optionsBase = this.element?.options ?? null;
    const overriddenBase = this.element?.overriddenOptionKeys ?? null;
    const brandingVersion = DynamicElement.#globalBrandingVersion;
    if (this.#overrideBadgeBase !== optionsBase
        || this.#overrideBadgeOverriddenBase !== overriddenBase
        || this.#overrideBadgeBrandingVersion !== brandingVersion) {
      this.#overrideBadgeBase = optionsBase;
      this.#overrideBadgeOverriddenBase = overriddenBase;
      this.#overrideBadgeBrandingVersion = brandingVersion;
      this.#overrideBadgeCache.clear();
    }
    const cacheKey = breakpointAlias || "default";
    if (this.#overrideBadgeCache.has(cacheKey)) {
      return this.#overrideBadgeCache.get(cacheKey);
    }
    const badges = overridesForBreakpoint({
      overriddenOptionKeysSet: this.overriddenOptionKeysSet,
      parsedOptions: this.parsedOptions,
      breakpointAlias: cacheKey,
      hasBrandingFallback: optionKey => this.hasBrandingFallback(optionKey, "default", "default"),
    });
    this.#overrideBadgeCache.set(cacheKey, badges);
    return badges;
  }

  get hasAnyOverrides() {
    return this.overriddenOptionKeysSet.size > 0;
  }

  getRawOptionValue(optionKey, breakpoint, state) {
    return this.#resolveAndCache(optionKey, breakpoint, state);
  }

  getOptionValuesBundle(optionKeys, breakpoint, state) {
    if (!this.element) {
      return {};
    }
    return optionKeys.reduce((bundle, key) => {
      bundle[key] = this.#resolveAndCache(key, breakpoint, state);
      return bundle;
    }, {});
  }

  optionCanBeInherited(optionKey, breakpoint, state) {
    const data = this.#elementDataForOption();
    if (!data) {
      return false;
    }
    return DynamicOption.optionCanBeInherited(
      data, optionKey, this.#breakpointAlias(breakpoint), state, this.#optionContext(),
    );
  }

  #elementDataForOption(optionsOverride = null) {
    if (!this.element) {
      return null;
    }
    const isTheme = this.isTheme;
    const brandedType = isTheme ? this.#brandingConfigKeyForEntity() : undefined;
    return {
      id: this.id,
      dynamicComponentId: this.dynamicComponentId ?? null,
      type: this.element.type,
      brandedVariant: this.element.brandedVariant,
      brandedSize: this.element.brandedSize,
      elementTemplateId: this.element.elementTemplateId,
      options: optionsOverride ?? this.parsedOptions,
      elementType: isTheme ? "theme" : "element",
      brandedType,
      destinationId: this.destinationId ?? null,
    };
  }

  #brandingConfigKeyForEntity() {
    const elementType = this.element?.type;
    if (!elementType) {
      return undefined;
    }
    const elementDef = DynamicElement.getElementDefinition(elementType);
    const BrandingClass = elementDef?.brandingType ?? null;
    if (!BrandingClass) {
      return undefined;
    }
    return BrandingClass.brandingConfigKey ?? elementType;
  }

  #breakpointAlias(breakpoint) {
    if (!breakpoint) {
      return "default";
    }
    if (typeof breakpoint === "string") {
      return breakpoint;
    }
    return breakpoint.alias ?? "default";
  }

  #pruneCorruptTreeKeys(tree) {
    if (!tree || typeof tree !== "object") {
      return tree;
    }
    const allowedBreakpoints = this.#currentBreakpointAliasSet();
    const clean = {};
    for (const modeKey of Object.keys(tree)) {
      if (modeKey.startsWith("[object ")) {
        continue;
      }
      const modeVal = tree[modeKey];
      if (!modeVal || typeof modeVal !== "object") {
        clean[modeKey] = modeVal;
        continue;
      }
      const isModeTree = modeKey === "light-mode" || modeKey === "dark-mode";
      const cleanMode = {};
      for (const bpKey of Object.keys(modeVal)) {
        if (bpKey.startsWith("[object ")) {
          continue;
        }
        if (isModeTree && allowedBreakpoints && !allowedBreakpoints.has(bpKey)) {
          continue;
        }
        cleanMode[bpKey] = modeVal[bpKey];
      }
      clean[modeKey] = cleanMode;
    }
    return clean;
  }

  #currentBreakpointAliasSet() {
    let breakpoints;
    try {
      breakpoints = useDynamicComponentEditorStore().availableBreakpoints;
    } catch (error) {
      return null;
    }
    if (!Array.isArray(breakpoints) || breakpoints.length === 0) {
      return null;
    }
    const aliases = new Set(breakpoints.map(breakpoint => breakpoint?.alias).filter(Boolean));
    if (aliases.size === 0) {
      return null;
    }
    aliases.add("default");
    return aliases;
  }

  #resolveAndCache(optionKey, breakpoint, state) {
    if (!this.element) {
      return undefined;
    }
    const optionClass = DynamicElement.getOptionClass(this.type, optionKey);
    if (!optionClass) {
      return undefined;
    }
    const ctx = this.#optionContext();
    const bpAlias = this.#breakpointAlias(breakpoint);

    const dynamicComponent = ctx.dynamicComponent ?? DynamicComponent.get(this.dynamicComponentId);
    const variableId = dynamicComponent?.getVariableBindingForOption?.(this.#id, optionKey) ?? null;
    if (variableId !== null) {
      const raw = dynamicComponent.getVariableValue(variableId);
      if (raw !== undefined) {
        let compiled;
        try {
          compiled = optionClass.constructor.convertConfigValueToCompiledValue(raw, ctx.themeId, ctx.themeMode);
        } catch {
          compiled = undefined;
        }
        return { r: raw, c: compiled, i: false };
      }
    }

    const result = DynamicOption.resolveOptionValueWithCache({
      tree: this.parsedOptions,
      themeMode: ctx.themeMode,
      breakpoint: bpAlias,
      state,
      optionKey,
      optionClass,
      themeId: ctx.themeId,
      inheritanceResolver: (tree, mode, bp, st, key) => {
        return DynamicOption.getOptionValue(
          this.#elementDataForOption(tree),
          key,
          bp,
          st,
          { ...ctx, themeMode: mode },
        );
      },
    });

    if (result.cached) {
      this.#workingOptionsTree = result.tree;
    }
    return result.value;
  }

  get elementTemplateId() {
    return this.element?.elementTemplateId ?? this.element?.template?.componentId ?? null;
  }

  #optionContext() {
    if (!this.isTheme) {
      const dynamicComponent = DynamicComponent.get(this.dynamicComponentId);
      if (dynamicComponent) {
        return {
          themeId: dynamicComponent.themeId,
          themeMode: dynamicComponent.themeMode,
          breakpoints: dynamicComponent.breakpoints,
          theme: dynamicComponent.theme,
          dynamicComponent,
        };
      }
    }
    const themeId = useStagingFrameStore().themeId
      || DynamicComponent.defaultThemeId
      || null;
    return {
      themeId,
      themeMode: "light-mode",
      breakpoints: [],
      theme: themeId ? Theme.get(themeId) : null,
      dynamicComponent: null,
    };
  }

  getResolvedOptionLeaf(optionKey, breakpoint, state) {
    if (!this.element) {
      return undefined;
    }
    return DynamicOption.getOptionValue(this.#elementDataForOption(), optionKey, breakpoint, state, this.#optionContext());
  }

  getCompiledOptionValue(optionKey, breakpoint, state, themeMode, variableOverrides = null) {
    if (!this.element) {
      return undefined;
    }
    return this.#resolveCompiledOptionValue(optionKey, breakpoint, state, themeMode, variableOverrides);
  }

  #resolveCompiledOptionValue(optionKey, breakpoint, state, themeMode, variableOverrides) {
    const baseContext = this.#optionContext();
    const context = {
      ...baseContext,
      themeMode: themeMode ?? baseContext.themeMode,
      variableOverrides,
    };

    const dynamicComponent = baseContext.dynamicComponent ?? DynamicComponent.get(this.dynamicComponentId);
    const variableId = dynamicComponent?.getVariableBindingForOption?.(this.#id, optionKey) ?? null;
    if (variableId !== null) {
      const override = variableOverrides?.[variableId];
      const raw = override !== undefined ? override : dynamicComponent.getVariableValue(variableId);
      if (raw !== undefined) {
        const optionClass = DynamicElement.getOptionClass(this.type, optionKey);
        if (optionClass) {
          try {
            return optionClass.constructor.convertConfigValueToCompiledValue(raw, context.themeId, context.themeMode);
          } catch {
            return undefined;
          }
        }
      }
    }

    return DynamicOption.getCompiledOptionValue(
      this.#elementDataForOption(),
      optionKey,
      breakpoint,
      state,
      context,
    );
  }

  getOptionValue(optionKey, breakpoint, state, context) {
    if (!this.element) {
      return;
    }
    const themeMode = context?.themeMode ?? "light-mode";
    const optionClass = DynamicElement.getOptionClass(this.type, optionKey);
    if (!optionClass) {
      return;
    }

    const result = DynamicOption.resolveOptionValueWithCache({
      tree: this.parsedOptions,
      themeMode,
      breakpoint,
      state,
      optionKey,
      optionClass,
      themeId: context?.themeId ?? null,
      inheritanceResolver: (tree, mode, bp, st, key) => {
        return DynamicOption.getOptionValue(
          this.#elementDataForOption(tree),
          key,
          bp,
          st,
          { ...context, themeMode: mode },
        );
      },
    });

    if (result.cached) {
      this.#workingOptionsTree = result.tree;
    }
    return result.value;
  }

  retireOptionKey(optionKey) {
    if (!this.element) {
      return false;
    }
    const tree = this.parsedOptions;
    let found = false;
    for (const modeVal of Object.values(tree ?? {})) {
      for (const bpVal of Object.values(modeVal ?? {})) {
        for (const stateVal of Object.values(bpVal ?? {})) {
          if (stateVal && typeof stateVal === "object" && optionKey in stateVal) {
            delete stateVal[optionKey];
            found = true;
          }
        }
      }
    }
    if (!found) {
      return false;
    }
    const retiredMessage = `DynamicElement ${this.id} (${this.type}): retired option "${optionKey}" dropped from `
      + "this element as part of re-authoring the option that replaced it.";
    console.warn(retiredMessage); // eslint-disable-line no-console
    this.#workingOptionsTree = tree;
    this.#scheduleOptimisticOptionsWrite();
    this.#scheduleOptionsSave();
    return true;
  }

  updateOption(optionKey, breakpoint, state, rawOptionValue, context) {
    if (!this.element) {
      return;
    }
    assertDeclaredOptionWrite({
      elementId: this.id,
      elementType: this.type,
      optionKey,
      typeIsRegistered: Boolean(DynamicElement.getElementDefinition(this.type)),
      optionClass: DynamicElement.getOptionClass(this.type, optionKey),
      ErrorClass: DynamicComponentError,
    });

    if (this.#optionsSaveTimer === null && this.#undoBeforeOptions === null) {
      this.#undoBeforeOptions = {
        options: this.element.options ?? {},
        overriddenOptionKeys: this.element.overriddenOptionKeys ?? "[]",
      };
    }

    const effectiveContext = context ?? this.#optionContext();

    const breakpointAlias = this.#breakpointAlias(breakpoint);

    let tree = this.parsedOptions;

    ["light-mode", "dark-mode"].forEach(mode => {
      const processed = DynamicOption.processElementOption(
        this.element, optionKey, breakpointAlias, state, rawOptionValue, mode, effectiveContext,
      );
      tree = DynamicOption.setOptionValue(tree, mode, breakpointAlias, state, optionKey, processed);
    });

    tree = this.#pruneCorruptTreeKeys(tree);

    this.#workingOptionsTree = tree;
    this.#scheduleOptimisticOptionsWrite();
    this.#scheduleOptionsSave();
  }

  #hasUnsavedOptions() {
    return this.#optionsSaveTimer !== null || this.#optimisticWriteHandle !== null;
  }

  #scheduleOptimisticOptionsWrite() {
    if (this.#optimisticWriteHandle !== null) {
      return;
    }
    const flush = () => {
      this.#optimisticWriteHandle = null;
      this.#writeOptimisticOptions();
    };
    this.#optimisticWriteHandle = typeof requestAnimationFrame === "function"
      ? requestAnimationFrame(flush)
      : setTimeout(flush, 0);
  }

  #flushOptimisticOptionsWriteNow() {
    if (this.#optimisticWriteHandle === null) {
      return;
    }
    if (typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this.#optimisticWriteHandle);
    }
    clearTimeout(this.#optimisticWriteHandle);
    this.#optimisticWriteHandle = null;
    this.#writeOptimisticOptions();
  }

  #writeOptimisticOptions() {
    if (!this.element || typeof this.element.hydrateFields !== "function") {
      return;
    }
    const nextOptionsVersion = (this.element.optionsVersion ?? 0) + 1;
    this.#optimisticWriteTarget = this.element;
    this.element.hydrateFields(
      { options: this.#workingOptionsTree, optionsVersion: nextOptionsVersion },
      true,
    );
  }

  updateType(newType) {
    if (!this.element || this.element.isEntityPkTemp?.() || !newType || this.element.type === newType) {
      return;
    }
    App.get("CreatorStudio").triggerEvent("UpdateElementType", {
      element: { id: this.element.id },
      type: newType,
    });
  }

  #scheduleOptionsSave() {
    this.#brandingHasValueCache.clear();
    if (this.#optionsSaveTimer) {
      clearTimeout(this.#optionsSaveTimer);
    }
    this.#optionsSaveTimer = setTimeout(() => {
      this.#optionsSaveTimer = null;
      if (!this.element) {
        return;
      }
      this.#flushOptimisticOptionsWriteNow();
      if (this.#optimisticWriteTarget !== this.element) {
        this.#writeOptimisticOptions();
      }
      const payload = DynamicOption.stripUnpersistableValues(this.#workingOptionsTree || {});
      DynamicElement.carryContextBinding(this.#workingOptionsTree, payload);
      const overriddenOptionKeys = JSON.stringify(
        DynamicOption.collectOverriddenOptionKeys(payload, this.isTheme ? "theme" : "element"),
      );
      const afterOptions = payload;
      const afterOptionsKey = JSON.stringify(payload);
      const undoBefore = this.#undoBeforeOptions;
      this.#undoBeforeOptions = null;
      if (undoBefore
        && (JSON.stringify(undoBefore.options) !== afterOptionsKey || undoBefore.overriddenOptionKeys !== overriddenOptionKeys)) {
        useDynamicComponentEditorStore().recordOptionEditUndoUnit(
          this.element.getPk(),
          undoBefore,
          { options: afterOptions, overriddenOptionKeys },
        );
      }
      App.get("CreatorStudio").triggerEvent("UpdateElement", {
        element: { id: this.element.id },
        options: afterOptions,
        optionsVersion: this.element.optionsVersion ?? 0,
        type: this.type,
        displayName: this.element.displayName,
        overriddenOptionKeys,
      });
    }, OPTIONS_SAVE_DEBOUNCE_MS);
  }

  getOptionTools(context = {}) {
    const { themeId, breakpointAlias = "default" } = context;
    if (!this.elementDef) {
      return [];
    }

    let mapped = [];

    const bundles = DynamicOption.getElementOptionBundles(this.elementDef);
    const options = DynamicOption.getElementOptionClasses(this.elementDef);
    const variableClasses = this.getVariableOptionClasses();

    const bundlesWithUi = bundles.filter(b => b.userInterfaceComponent);
    const flattenedBundleOptions = bundles
        .filter(b => !b.userInterfaceComponent)
        .flatMap(b => b.options || []);

    const visibleVariableClasses = variableClasses.filter(o => !o.shouldHideFromInspector);
    const visibleFlattened = flattenedBundleOptions.filter(o => !o.shouldHideFromInspector);
    const visibleOptions = options.filter(o => !o.shouldHideFromInspector);

    mapped.push(...this.#mapBundlesToJson(bundlesWithUi, { themeId, breakpointAlias, ...context }));
    mapped.push(...this.#mapOptionsToJson(visibleVariableClasses, { themeId }));
    mapped.push(...this.#mapOptionsToJson(visibleFlattened, { themeId }));
    mapped.push(...this.#mapOptionsToJson(visibleOptions, { themeId }));

    if (this.type === "DynamicComponentElement") {
      mapped.push(...this.#mapVariableOverrideTools({ themeId }));
    }

    const topKeys = [
      "lock-options",
      "positioning",
      "appearance",
      "animations",
      "width height",
      "feed-v2selection",
      "story-folder-feed-selection",
    ];
    const bottomKeys = ["basic-text", "advanced-text", "padding", "margins"];

    const topSorted = this.#extractByOrder(mapped, topKeys);
    const bottomSorted = this.#extractByOrder(mapped, bottomKeys);

    return [...topSorted, ...mapped, ...bottomSorted];
  }

  #mapVariableOverrideTools(context) {
    const refIdRaw = this.getRawOptionValue("referenced-component-id", "default", "default");
    const refId = Number(refIdRaw?.r ?? refIdRaw);
    if (!Number.isFinite(refId) || refId === 0) {
      return [];
    }
    const refDc = DynamicComponent.get(refId);
    const variables = refDc?.variables ?? {};
    const optionsMap = OptionsMap.optionsMap;
    const tools = [];
    for (const [variableId, variable] of Object.entries(variables)) {
      const optionKey = variable?.optionKey;
      if (!optionKey) {
        continue;
      }
      const OptionClass = optionsMap[kebabToCamelCase(optionKey)];
      if (!OptionClass) {
        continue;
      }
      const inst = new OptionClass();
      inst.optionKey = `variable-overrides.${variableId}`;
      inst.overrideLabel = variable.label || optionKey;
      tools.push(inst);
    }
    return tools.filter(tool => tool.getConfigurationComponent({}, context.themeId) !== inspectorTools.NONE);
  }

  getVariableOptionClasses() {
    if (this.element?.template?.componentId) {
      const related = DynamicComponent.get(this.element.template.componentId);
      if (typeof related.getVariableClasses === "function") {
        return related.getVariableClasses();
      }
    }
    return [];
  }

  #extractByOrder(items, orderKeys) {
    const sorted = [];
    orderKeys.forEach(optionKey => {
      const index = items.findIndex(option => option.optionKey === optionKey);
      if (index > -1) {
        sorted.push(...items.splice(index, 1));
      }
    });
    return sorted;
  }

  #mapBundlesToJson(bundles, context) {
    return bundles.filter(bundle => bundle.userInterfaceComponent).map(bundle => {
      const mappedBundleOptions = this.#mapOptionsToJson(bundle.options, context);

      const BundleOptionClass = createOptionClass({
        name: camelToTitleCase(bundle.inspectorToolGroup || ""),
        label: bundle.useInspectorLabel ? bundle.optionTitle : "",
        userInterfaceComponent: bundle.userInterfaceComponent,
        bundleOptions: mappedBundleOptions,
      });

      const inst = new BundleOptionClass();
      inst.optionTitle = bundle.optionTitle;
      inst.isOverridable = bundle.isOverridable;
      inst.isBundled = true;
      return inst;
    });
  }

  #mapOptionsToJson(options, context) {
    return options.filter(option => {
      if (option.optionKey === "option-variables") {
        return false;
      }
      return option.getConfigurationComponent({}, context.themeId) !== inspectorTools.NONE;
    }).map(option => {
      option.disabled = !!option.shouldHideFromInspector?.disable;
      return option;
    });
  }
}

export default DynamicElement;
